import { useId, type ReactNode } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, type SelectChangeEvent, type SelectProps, type SxProps, type Theme } from '@mui/material';

export type HFSelectOption = { value: string | number; label: ReactNode; disabled?: boolean; hint?: string };

export type HFSelectProps = Omit<SelectProps, 'onChange'> & {
  label?: ReactNode;
  options?: HFSelectOption[];
  onChange?: (value: string) => void;
  formControlSx?: SxProps<Theme>;
};

export function HFSelect({
  label,
  options,
  onChange,
  children,
  fullWidth = true,
  size = 'small',
  variant = 'outlined',
  formControlSx,
  displayEmpty,
  value,
  MenuProps,
  renderValue,
  ...props
}: HFSelectProps) {
  const labelId = useId();
  const shrinkLabel = Boolean(displayEmpty) || (value !== '' && value != null);
  const hinted = options?.some((option) => option.hint);

  return (
    <FormControl fullWidth={fullWidth} size={size} variant={variant} disabled={props.disabled} sx={formControlSx}>
      {label != null ? (
        <InputLabel id={labelId} shrink={shrinkLabel || undefined}>
          {label}
        </InputLabel>
      ) : null}
      <Select
        labelId={label != null ? labelId : undefined}
        label={label}
        size={size}
        variant={variant}
        displayEmpty={displayEmpty}
        value={value}
        notched={label != null ? shrinkLabel : undefined}
        {...props}
        MenuProps={
          hinted
            ? {
                ...MenuProps,
                slotProps: {
                  ...MenuProps?.slotProps,
                  paper: {
                    style: { minWidth: 320 },
                  },
                },
              }
            : MenuProps
        }
        renderValue={
          renderValue ??
          (hinted
            ? (selected) => {
                const option = options?.find((item) => String(item.value) === String(selected));
                return option ? option.label : '';
              }
            : undefined)
        }
        onChange={(event: SelectChangeEvent<unknown>) => onChange?.(String(event.target.value))}
      >
        {renderSelectOptions(options)}
        {children}
      </Select>
    </FormControl>
  );
}

export { MenuItem as HFMenuItem };

function renderSelectOptions(options: HFSelectOption[] | undefined) {
  if (!options?.length) return null;
  return options.map((option) => (
    <MenuItem key={String(option.value)} value={option.value} disabled={option.disabled} dense>
      {option.hint ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%' }}>
          <Box component="span" sx={{ minWidth: 0 }}>
            {option.label}
          </Box>
          <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, flex: '0 0 auto' }}>
            {option.hint}
          </Box>
        </Box>
      ) : (
        option.label
      )}
    </MenuItem>
  ));
}
